import axios from '../utils/setupAxiosAuth';
import { getAdminApiBaseUrl } from '../utils/adminApiBaseUrl';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { getAuthHeaders } from '../utils/authHeaders';

export type SocialCampaignOption = {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  event_count: number;
};

export type SocialCampaignInquiryItem = {
  inquiry_id: number;
  inquiry_value: number;
  occurred_at: string | null;
  product_id: number | null;
  inquiry_status: string | null;
  visitor_id?: number | null;
  registered_user_id?: number | null;
  customer_id?: number | null;
  is_new_customer?: boolean;
  is_new_inquiry?: boolean;
};

export type SocialCampaignWebVisitItem = {
  visitor_id: number | null;
  registered_user_id: number | null;
  phone_mobile: string | null;
  is_registered: boolean;
  first_visit_at: string | null;
  last_visit_at: string | null;
  has_attributed_install: boolean;
  device_uuid?: string | null;
  install_attributed_at?: string | null;
  is_new_visitor?: boolean;
  is_new_customer?: boolean;
};

export type SocialCampaignRow = {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  redirect_landings?: number;
  store_redirects?: number;
  attributed_installs?: number;
  store_listing_visitors?: number;
  google_play_acquisitions?: number;
  apple_first_time_downloads?: number;
  store_acquisitions?: number;
  store_acquisitions_estimated?: boolean;
  funnel_app_opens?: number;
  funnel_web_visits?: number;
  total_downloads?: number;
  visits: number;
  registered: number;
  wishlist: number;
  inquiries: number;
  inquiry_items?: SocialCampaignInquiryItem[];
  web_visit_items?: SocialCampaignWebVisitItem[];
  orders: number;
  inquiry_value_total: number;
  order_value_total: number;
  lead_value_total: number;
  visit_to_inquiry_rate: number;
  total_customers?: number;
  new_customers?: number;
  new_inquiries?: number;
  new_inquiry_value_total?: number;
  budget_amount?: number | null;
  roi_percent?: number | null;
  roas?: number | null;
  marketing_activity_id?: number | null;
  spend_notes?: string | null;
};

export type StoreSyncStatus = {
  provider: 'google_play' | 'apple_app_store';
  status: 'never' | 'running' | 'success' | 'error' | 'not_configured';
  last_started_at?: string | null;
  last_completed_at?: string | null;
  rows_imported: number;
  message?: string | null;
};

export type SocialCampaignResponse = {
  campaigns: SocialCampaignRow[];
  campaign_options?: SocialCampaignOption[];
  store_sync_status?: StoreSyncStatus[];
  totals: {
    redirect_landings?: number;
    store_redirects?: number;
    attributed_installs?: number;
    store_listing_visitors?: number;
    google_play_acquisitions?: number;
    apple_first_time_downloads?: number;
    store_acquisitions?: number;
    total_downloads?: number;
    visits: number;
    registered: number;
    wishlist: number;
    inquiries: number;
    orders: number;
    inquiry_value_total: number;
    order_value_total: number;
    lead_value_total: number;
    total_customers?: number;
    new_customers?: number;
    new_inquiries?: number;
    new_inquiry_value_total?: number;
  };
};

export type StoreSyncResult = {
  provider?: 'google_play' | 'apple_app_store';
  status: string;
  rowsImported?: number;
  message?: string;
};

export type MarketingActivity = {
  id: number;
  activity_name: string;
  activity_type: string;
  channel: string;
  campaign_name: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  budget_amount: number | null;
  notes: string | null;
};

export const fetchSocialCampaigns = async (
  fromDate: string,
  toDate: string,
  utmCampaign?: string
): Promise<SocialCampaignResponse> => {
  const apiBase = getAdminApiBaseUrl().replace(/\/?$/, '/');
  const response = await axios.get(`${apiBase}customers/social-campaigns`, {
    params: {
      from_date: fromDate,
      to_date: toDate,
      utm_campaign: utmCampaign || undefined,
    },
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const syncStoreAcquisitions = async (
  fromDate: string,
  toDate: string
): Promise<StoreSyncResult[]> => {
  const apiBase = getAdminApiBaseUrl().replace(/\/?$/, '/');
  const response = await axios.post(
    `${apiBase}customers/social-campaigns/store-sync`,
    { from_date: fromDate, to_date: toDate },
    { headers: getAuthHeaders() }
  );
  return response.data?.results || [];
};

export const fetchMarketingActivities = async (): Promise<MarketingActivity[]> => {
  const authHeaders = getAuthHeaders();
  if (!authHeaders.Authorization) {
    return [];
  }

  const apiBase = getApiBaseUrl().replace(/\/?$/, '/');
  const response = await axios.get(`${apiBase}marketingactivities`, {
    params: {
      _start: 0,
      _end: 500,
      _sort: 'dt_created',
      _order: 'DESC',
    },
    headers: authHeaders,
  });
  return response.data || [];
};

export const saveMarketingActivity = async (
  payload: Partial<MarketingActivity> & { utm_campaign: string; activity_name: string }
): Promise<MarketingActivity> => {
  const apiBase = getApiBaseUrl().replace(/\/?$/, '/');
  const headers = getAuthHeaders();
  if (payload.id) {
    const response = await axios.put(`${apiBase}marketingactivities/${payload.id}`, payload, { headers });
    return response.data;
  }
  const response = await axios.post(`${apiBase}marketingactivities`, payload, { headers });
  return response.data;
};

export const mergeCampaignSpend = (
  campaigns: SocialCampaignRow[],
  activities: MarketingActivity[]
): SocialCampaignRow[] => {
  const spendByCampaign = activities.reduce<Record<string, number>>((acc, activity) => {
    if (!activity.utm_campaign || !activity.budget_amount) {
      return acc;
    }
    acc[activity.utm_campaign] = (acc[activity.utm_campaign] || 0) + Number(activity.budget_amount);
    return acc;
  }, {});

  const activityByCampaign = activities.reduce<Record<string, MarketingActivity>>((acc, activity) => {
    if (!activity.utm_campaign) {
      return acc;
    }
    const existing = acc[activity.utm_campaign];
    if (!existing || Number(activity.id || 0) > Number(existing.id || 0)) {
      acc[activity.utm_campaign] = activity;
    }
    return acc;
  }, {});

  return campaigns.map((campaign) => {
    const budget = spendByCampaign[campaign.utm_campaign] ?? null;
    const activity = activityByCampaign[campaign.utm_campaign];
    const leadValue = campaign.lead_value_total || 0;
    const roiPercent = budget && budget > 0
      ? Number((((leadValue - budget) / budget) * 100).toFixed(2))
      : null;
    const roas = budget && budget > 0
      ? Number((leadValue / budget).toFixed(2))
      : null;

    return {
      ...campaign,
      budget_amount: budget,
      marketing_activity_id: activity?.id ?? null,
      spend_notes: activity?.notes ?? null,
      roi_percent: roiPercent,
      roas,
    };
  });
};
